/**
 * 이상한 방 - 메뉴 화면 (순수 DOM)
 *
 * 근거 문서: 로드맵 M13 · `ui/levelSelect.ts` 머리말("이 화면의 목적은 다음 한 판을 손가락 밑에 놓는 것")
 *
 * ## 왜 생겼나
 *
 * [시작하기] 뒤에 서던 화면은 단계 선택 하나였고, 거기에 세 모드(매일의 방 · 10단계 · 여럿이)와
 * 계정 줄(핀 · 봉투 · 사물함)이 전부 얹혀 있었다. 폰 세로 375×812에서 **단계 목록에 남는 높이가
 * 326px**이었다 - 열 단계 중 세 줄 반. `levelSelect.ts` 머리말이 스스로 적어 둔 경고("요소 하나를
 * 더할 때마다 그 목적과 경쟁한다")가 M3·M6·M9를 거치며 현실이 된 것이다.
 *
 * 그래서 이 화면은 **새 기능이 아니라 그 머리말이 원래 요구한 상태로 되돌리는 일**이다. 메뉴는
 * "무엇을 할 것인가"만 묻고, 단계 선택은 단계만 남는다.
 *
 * ## 계산하지 않는다
 *
 * 카드의 상태 문구(오늘 것을 했는가 · 몇 단계를 깼는가 · 봉투를 열 수 있는가)는 전부 **받은 뷰**다.
 * 여기서 "오늘인가"나 "핀이 모자란가"를 다시 판단하면 규칙이 두 벌이 되고, 그중 하나가 조용히
 * 관대해진다(`ui/lobby.ts`·`ui/dailyCard.ts`가 같은 규약을 적고 있다).
 *
 * ## 카드는 여섯이고 두 종류다
 *
 * 큰 셋(매일의 방 · 혼자 연습 · 모두의 방)은 **한 판을 여는 문**이고, 작은 셋(봉투 · 사물함 · 설정)은
 * **서랍**이다 - 누른다고 판이 시작되지 않는다. 물성을 다르게 준 이유가 그것뿐이라, 서랍이 판을
 * 열기 시작하면 이 구분은 없어져야 한다.
 *
 * ## 설정은 이미 있는 패널을 연다
 *
 * 두 번째 설정 화면을 만들지 않는다(M13 규칙 3). 값이 두 곳에 사는 순간 갈리고, 톱니는 라운드
 * 중에도 열려야 하므로 어차피 남는다 - 이 카드는 그 톱니와 **같은 패널**을 여는 두 번째 손잡이다.
 *
 * ## 문구는 만들 때가 아니라 그릴 때 굽는다
 *
 * `show`마다 다시 칠한다. 언어 전환에서 `main.rebuildOverlays`가 이 화면을 통째로 다시 세우지만,
 * 굳은 문구가 남을 자리를 아예 두지 않는 편이 검사가 필요 없어서 낫다(`ui/dailyCard.ts`와 같은 결정).
 */

import { t } from '../i18n'

import { createDailyCard, type DailyCardView } from './dailyCard'
import { createElement, setText } from './dom'
import { createPinsChip } from './pinsChip'
import './menu.css'

/* --- 입력 계약 ----------------------------------------------------------------- */

/** 혼자 연습 카드가 말하는 전부. 진행의 규칙은 `game/progress.ts`가 정하고 여기는 받은 수를 적는다. */
export type MenuSolo = Readonly<{
  /** 지금 할 단계. 전부 깼으면 `null`이고, 그때 카드는 단계 번호 대신 졸업을 말한다. */
  nextLevel: number | null
  cleared: number
  total: number
}>

/**
 * 봉투 서랍이 말하는 전부. 값의 규칙(한 장이 얼마인가 · 몇 판이면 열리나)은 `game/economy/pins.ts`가
 * 정하고 여기서는 받은 답만 그린다 - 이 화면이 `spendPins`를 부르기 시작하면 결제 판정이 두 곳에 생긴다.
 */
export type MenuAccount = Readonly<{
  pins: number
  envelopePrice: number
  /** 봉투까지 모자란 핀. 0이면 열 수 있다. */
  shortBy: number
}>

/** 모두의 방 카드가 지킬 수 있는 약속. `LevelSelectParty`가 쥐고 있던 것과 같은 값이다. */
export type MenuParty = Readonly<{ seatsMax: number }>

export type MenuView = Readonly<{
  daily: DailyCardView
  solo: MenuSolo
  account: MenuAccount
}>

export type MenuHandlers = Readonly<{
  onDaily: () => void
  onSolo: () => void
  onParty: () => void
  onEnvelope: () => void
  onLocker: () => void
  onSettings: () => void
}>

export type MenuOptions = Readonly<{
  mount: HTMLElement
  handlers: MenuHandlers
  party: MenuParty
}>

export type MenuHandle = Readonly<{
  show: (view: MenuView) => void
  hide: () => void
  dispose: () => void
}>

/* --- 카드 조립 ----------------------------------------------------------------- */

/** 큰 카드 하나의 손잡이. 만든 쪽이 `show`마다 칠한다(문구를 굽지 않는다는 규약). */
type CardParts = Readonly<{
  root: HTMLButtonElement
  eyebrow: HTMLElement
  title: HTMLElement
  meta: HTMLElement
  status: HTMLElement
  note: HTMLElement
  go: HTMLElement
}>

/**
 * 큰 카드 하나. **전체가 버튼 하나다** - 읽을 것은 제목·칩·한두 줄이고 누를 것은 하나뿐이라,
 * 안에 작은 [들어가기]를 또 두면 눌리는 면적만 줄어든다(`ls-party__card`가 세운 문법 그대로).
 */
function createCard(onClick: () => void): CardParts {
  const root = createElement('button', 'mn-card') as HTMLButtonElement
  root.type = 'button'

  const eyebrow = createElement('span', 'mn-card__eyebrow', '')
  eyebrow.setAttribute('aria-hidden', 'true')

  const body = createElement('span', 'mn-card__body')
  body.setAttribute('aria-hidden', 'true')
  const title = createElement('span', 'mn-card__title', '')
  const meta = createElement('span', 'mn-card__meta')
  const status = createElement('span', 'mn-card__status', '')
  const note = createElement('span', 'mn-card__note', '')
  body.append(title, meta, status, note)

  const go = createElement('span', 'mn-card__go', '')
  go.setAttribute('aria-hidden', 'true')

  root.append(eyebrow, body, go)
  root.addEventListener('click', onClick)
  return { root, eyebrow, title, meta, status, note, go }
}

/** 칩 목록을 갈아 끼운다. 빈 칩은 만들지 않는다 - 값 없는 테두리는 정보가 아니라 잡음이다. */
function paintChips(meta: HTMLElement, texts: readonly string[]): void {
  meta.replaceChildren()
  for (const text of texts) meta.append(createElement('span', 'mn-chip', text))
}

/** 작은 서랍 하나. 이름 한 줄 + 값 한 줄이고, 값은 없어도 자리를 지킨다(있고 없고에 높이가 튀지 않게). */
type TileParts = Readonly<{ root: HTMLButtonElement; name: HTMLElement; value: HTMLElement }>

function createTile(onClick: () => void): TileParts {
  const root = createElement('button', 'mn-tile') as HTMLButtonElement
  root.type = 'button'
  const name = createElement('span', 'mn-tile__name', '')
  name.setAttribute('aria-hidden', 'true')
  const value = createElement('span', 'mn-tile__value', '')
  value.setAttribute('aria-hidden', 'true')
  root.append(name, value)
  root.addEventListener('click', onClick)
  return { root, name, value }
}

/* --- 본체 ---------------------------------------------------------------------- */

/**
 * 메뉴를 만들어 `mount`에 붙인다. 처음에는 숨겨져 있다.
 * 언제 띄울지는 `main.ts`의 몫이고, 이 화면은 자기가 먼저 나서지 않는다(`levelSelect`와 같은 규약).
 */
export function createMenu(options: MenuOptions): MenuHandle {
  const { handlers } = options

  const root = createElement('div', 'mn-root')
  root.hidden = true
  root.setAttribute('role', 'dialog')
  root.setAttribute('aria-modal', 'true')
  root.setAttribute('aria-labelledby', 'mn-title')

  const head = createElement('header', 'mn-head')
  const eyebrow = createElement('p', 'mn-eyebrow', '')
  const title = createElement('h1', 'mn-title', '')
  title.id = 'mn-title'
  const pinsChip = createPinsChip()
  head.append(eyebrow, title, pinsChip.root)

  const cards = createElement('div', 'mn-cards')

  /*
   * 매일의 방 카드. **저쪽 파일이 그대로 옮겨 왔다**(로드맵 M13) - 단계 선택 맨 위에 있던 것이
   * 이제 메뉴의 첫 카드다. 카드가 말하는 것도 규칙도 한 글자 안 바뀌었고 바뀐 것은 사는 곳뿐이다.
   */
  const daily = createDailyCard(handlers.onDaily)

  const solo = createCard(handlers.onSolo)
  const party = createCard(handlers.onParty)
  cards.append(daily.root, solo.root, party.root)

  const tiles = createElement('div', 'mn-tiles')
  const envelope = createTile(handlers.onEnvelope)
  const locker = createTile(handlers.onLocker)
  const settings = createTile(handlers.onSettings)
  tiles.append(envelope.root, locker.root, settings.root)

  root.append(head, cards, tiles)
  options.mount.append(root)

  /* --- 그리기 --------------------------------------------------------------- */

  function paintSolo(view: MenuSolo): void {
    const graduated = view.nextLevel === null
    setText(solo.eyebrow, t('menu.solo.eyebrow'))
    setText(solo.title, t('menu.solo.title'))
    /*
     * 다 깬 사람에게 "다음은 1단계"라고 말할 수 없다. 그때 카드가 가리켜야 할 곳은 다음 단계가
     * 아니라 여럿이다(`select.footGraduated`가 같은 말을 한다) - 칩에서 단계 번호를 뺀다.
     */
    paintChips(solo.meta, graduated ? [] : [t('menu.solo.chipNext', { step: view.nextLevel ?? 0 })])
    setText(solo.status, t('select.progress', { cleared: view.cleared, total: view.total }))
    setText(solo.note, graduated ? t('select.footGraduated') : t('menu.solo.desc'))
    setText(solo.go, t('menu.go'))
    solo.root.setAttribute(
      'aria-label',
      t('menu.cardAria', { title: t('menu.solo.title'), status: t('select.progress', { cleared: view.cleared, total: view.total }) }),
    )
  }

  function paintParty(): void {
    setText(party.eyebrow, t('party.card.eyebrow'))
    setText(party.title, t('menu.party.title'))
    paintChips(party.meta, [t('party.card.chipSeats', { max: options.party.seatsMax })])
    setText(party.status, t('menu.party.desc'))
    setText(party.note, '')
    setText(party.go, t('menu.go'))
    party.root.setAttribute('aria-label', t('menu.cardAria', { title: t('menu.party.title'), status: t('menu.party.desc') }))
  }

  function paintTiles(account: MenuAccount): void {
    pinsChip.set(account.pins)

    // 잠긴 버튼은 이유를 말한다(GLOSSARY 3절). 모자란 수는 경제 모듈이 센 값(`shortBy`)을 그대로 적는다.
    const ready = account.shortBy === 0
    setText(envelope.name, t('envelope.open'))
    setText(envelope.value, ready ? t('envelope.price', { price: account.envelopePrice }) : t('pins.gauge.until', { count: account.shortBy }))
    envelope.root.disabled = !ready
    envelope.root.setAttribute('aria-label', t('envelope.openAria', { price: account.envelopePrice }))

    setText(locker.name, t('locker.open'))
    setText(locker.value, '')
    locker.root.setAttribute('aria-label', t('locker.open'))

    setText(settings.name, t('settings.title'))
    setText(settings.value, '')
    settings.root.setAttribute('aria-label', t('settings.title'))
  }

  return {
    show(view: MenuView): void {
      setText(eyebrow, t('menu.eyebrow'))
      setText(title, t('menu.title'))
      daily.render(view.daily)
      paintSolo(view.solo)
      paintParty()
      paintTiles(view.account)
      root.hidden = false

      /*
       * 첫 카드에 포커스를 준다. `aria-modal` 대화상자를 열어 놓고 포커스를 바깥에 두면
       * 낭독기가 판이 열린 것을 모른다(`levelSelect.show`와 같은 규약).
       */
      const first = root.querySelector<HTMLButtonElement>('.mn-card:not(.is-locked)')
      first?.focus()
    },

    hide(): void {
      // 숨긴 화면 안에 포커스가 남으면 키보드 탐색이 보이지 않는 버튼을 계속 밟는다.
      if (root.contains(document.activeElement)) {
        const focused = document.activeElement
        if (focused instanceof HTMLElement) focused.blur()
      }
      root.hidden = true
    },

    dispose(): void {
      daily.dispose()
      pinsChip.dispose()
      root.remove()
    },
  }
}
